/*
 * forms.js — the fields a PDF already has, made fillable.
 *
 * WHY THIS IS A SEPARATE THING FROM TEXT
 * ─────────────────────────────────────
 * A form field is not text drawn on a page: it is an interactive object with a
 * name, a type and a value, and filling it means SETTING THAT VALUE, not
 * stamping characters over the box. The difference is invisible until someone
 * opens the result in Acrobat and finds a document that cannot be re-filled,
 * or a government portal rejects it because the field is empty while the page
 * looks complete.
 *
 * pdf.js gives the geometry and current values (getAnnotations); pdf-lib writes
 * them back through the real form API. This module is the pure part in between:
 * turning annotations into field descriptors the editor can lay an input over.
 *
 * ⚠️ THE CYRILLIC TRAP, AGAIN. `form.updateFieldAppearances()` defaults to
 * Helvetica, which is WinAnsi and throws on «Иванов». A form filled in Russian
 * therefore fails at save time unless a covering face is passed in — see
 * fontForValues().
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.PdfEditorForms = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    /* pdf.js reports the widget kind as a two-letter PDF name. */
    var KIND = { Tx: 'text', Btn: 'button', Ch: 'choice', Sig: 'signature' };

    /*
     * describe(annotations, page) -> [{ id, name, kind, checkbox, radio,
     *                                  options, value, readOnly, placement }]
     *
     * `page` is {width, height, rotate} — the same shape the session model and
     * textlayer use, so a field box lands in exactly the coordinate system
     * every other object already lives in.
     */
    function describe(annotations, page, pdfToDisplay) {
        var out = [];
        (annotations || []).forEach(function (a, i) {
            if (!a || !a.fieldType) return;                 // links, popups, stamps
            if (a.hidden) return;
            var kind = KIND[a.fieldType] || 'other';
            if (kind === 'other') return;

            var r = a.rect || [0, 0, 0, 0];
            var placement = rectToPlacement(r, page, pdfToDisplay);
            if (!(placement.nw > 0 && placement.nh > 0)) return;

            out.push({
                id: 'f' + i,
                name: a.fieldName || ('field_' + i),
                kind: kind,
                // A checkbox and a radio are both /Btn; the difference is
                // whether the widget belongs to a radio group.
                checkbox: kind === 'button' && !a.radioButton && !a.pushButton,
                radio: !!a.radioButton,
                pushButton: !!a.pushButton,
                options: (a.options || []).map(function (o) {
                    return typeof o === 'string' ? o : (o.displayValue || o.exportValue || '');
                }),
                multiline: !!a.multiLine,
                maxLen: a.maxLen || 0,
                value: normaliseValue(a.fieldValue),
                readOnly: !!a.readOnly,
                required: !!a.required,
                placement: placement,
            });
        });
        return out;
    }

    function normaliseValue(v) {
        if (v === null || v === undefined) return '';
        if (Array.isArray(v)) return v.length ? String(v[0]) : '';
        return String(v);
    }

    /* A widget rect is [x1,y1,x2,y2] in PDF space, and the corners are not
       guaranteed to arrive in any particular order — some producers write them
       bottom-right first, which yields a negative box and an invisible field. */
    function rectToPlacement(r, page, pdfToDisplay) {
        var x0 = Math.min(r[0], r[2]), x1 = Math.max(r[0], r[2]);
        var y0 = Math.min(r[1], r[3]), y1 = Math.max(r[1], r[3]);
        var rot = ((page.rotate % 360) + 360) % 360;
        var pts = [[x0, y0], [x1, y0], [x0, y1], [x1, y1]]
            .map(function (p) { return pdfToDisplay(p[0], p[1], page.width, page.height, rot); });
        var xs = pts.map(function (p) { return p[0]; });
        var ys = pts.map(function (p) { return p[1]; });
        var dispW = (rot === 90 || rot === 270) ? page.height : page.width;
        var dispH = (rot === 90 || rot === 270) ? page.width : page.height;
        var minX = Math.min.apply(null, xs), maxX = Math.max.apply(null, xs);
        var minY = Math.min.apply(null, ys), maxY = Math.max.apply(null, ys);
        return {
            nx: minX / dispW, ny: minY / dispH,
            nw: (maxX - minX) / dispW, nh: (maxY - minY) / dispH,
            rot: 0,
        };
    }

    /*
     * Which of our faces can render everything the user typed into the form.
     * `updateFieldAppearances` needs ONE font for the whole form, so the answer
     * is the face that covers the concatenation — not per field.
     */
    function fontForValues(values, Fonts) {
        if (!Fonts) return null;
        var all = Object.keys(values || {}).map(function (k) { return String(values[k]); }).join(' ');
        if (!all.trim()) return 'noto';
        return Fonts.pick(all, 'noto') || 'noto';
    }

    /*
     * Values the user actually changed. Writing back everything would rewrite
     * fields the user never touched, which on a form with computed or
     * pre-filled values is a way to lose data that was already correct.
     */
    function changedValues(fields, edited) {
        var out = {};
        (fields || []).forEach(function (f) {
            if (f.readOnly) return;
            if (!Object.prototype.hasOwnProperty.call(edited, f.name)) return;
            var next = edited[f.name];
            if (String(next) === String(f.value)) return;
            out[f.name] = next;
        });
        return out;
    }

    /* A one-line summary for the UI: «3 поля, 1 заполнено». */
    function summarise(fields, edited) {
        var fillable = (fields || []).filter(function (f) { return !f.readOnly && !f.pushButton; });
        var filled = fillable.filter(function (f) {
            var v = Object.prototype.hasOwnProperty.call(edited || {}, f.name) ? edited[f.name] : f.value;
            return String(v || '').trim() !== '' && String(v) !== 'Off';
        });
        return { total: fillable.length, filled: filled.length };
    }

    return {
        describe: describe,
        rectToPlacement: rectToPlacement,
        fontForValues: fontForValues,
        changedValues: changedValues,
        summarise: summarise,
    };
});
